/**
 * Helper functions and constants extracted from ActivityDataEntryForm.
 * These are pure functions and static config used by the form and its sub-components.
 */
import type { GHGScope, ResolveFactorCandidate } from '../types';
import type { FactorSelectionMode, Scope2EnergyType } from '../types/activity-data';
import { SCOPE3_CATEGORIES_META } from '../types/ghg-settings';

// ─── Constants ────────────────────────────────────────────────────────────────

export const NO_SELECTION = '__none__';
export const DISCOVERED_ACTIVITY_VALUE = '__discovered_activity__';
export const DEFAULT_BOOTSTRAP_PER_METHOD_LIMIT = 500;

export const SUPPORTED_METHODS = [
  'Area',
  'AreaOverTime',
  'ContainerOverDistance',
  'Data',
  'DataOverTime',
  'Distance',
  'DistanceOverTime',
  'Energy',
  'Money',
  'Number',
  'NumberOverTime',
  'PassengerOverDistance',
  'Power',
  'Time',
  'Volume',
  'Weight',
  'WeightOverDistance',
  'WeightOverTime',
] as const;

export const SCOPE2_ALLOWED_METHODS = ['Energy', 'Money'] as const;
const SCOPE2_ALLOWED_METHOD_SET = new Set<string>(SCOPE2_ALLOWED_METHODS);

export const FALLBACK_SCOPE2_COUNTRY_CODES = [
  'US', 'GB', 'DE', 'FR', 'IT', 'ES', 'NL', 'PL', 'SE', 'NO',
  'DK', 'FI', 'CA', 'AU', 'JP', 'IN', 'BR', 'MX', 'AE', 'ZA',
] as const;

const COUNTRY_NAME_RESOLVER =
  typeof Intl !== 'undefined' && typeof Intl.DisplayNames !== 'undefined'
    ? new Intl.DisplayNames(
        typeof navigator !== 'undefined' && navigator.languages.length > 0
          ? navigator.languages
          : undefined,
        { type: 'region' }
      )
    : null;

export const SCOPE3_CATEGORY_OPTIONS = SCOPE3_CATEGORIES_META.map((category) => ({
  value: String(category.number),
  label: `${String(category.number)} - ${category.name}`,
}));

// ─── Types ────────────────────────────────────────────────────────────────────

export type ActivityChoiceSource = 'curated' | 'org_catalog' | 'discovered' | 'existing' | 'manual';

export interface ActivityChoice {
  value: string;
  label: string;
  activityId: string;
  unitType: string;
  defaultUnit?: string;
  allowedUnits?: string[];
  notes?: string;
  source: ActivityChoiceSource;
  presetId?: string;
}

// ─── Pure Helper Functions ────────────────────────────────────────────────────

export function isMethodAllowedForScope(scope: GHGScope, method: string): boolean {
  if (scope !== 2) return true;
  return SCOPE2_ALLOWED_METHOD_SET.has(method);
}

export function formatMethodLabel(method: string): string {
  return method.replace(/([a-z])([A-Z])/g, '$1 $2');
}

export function formatActivityCountLabel(count: number): string {
  return `${String(count)} ${count === 1 ? 'activity' : 'activities'}`;
}

export function factorSelectionModeLabel(mode: FactorSelectionMode | undefined): string {
  if (mode === 'manual') return 'Manually selected';
  if (mode === 'auto') return 'Auto-selected';
  return 'Pending selection';
}

export function factorSelectionModeClasses(mode: FactorSelectionMode | undefined): string {
  if (mode === 'manual') return 'bg-amber-100 text-amber-900';
  if (mode === 'auto') return 'bg-emerald-100 text-emerald-900';
  return 'bg-muted text-muted-foreground';
}

export function shortenFactorId(id: string): string {
  if (id.length <= 20) return id;
  return `${id.slice(0, 8)}...${id.slice(-6)}`;
}

export function formatCountryOption(code: string): string {
  const normalized = code.trim().toUpperCase();
  if (!normalized) return '';
  const displayName = COUNTRY_NAME_RESOLVER?.of(normalized);
  return displayName ? `${displayName} (${normalized})` : normalized;
}

export function normalizeScope2EnergyType(value: string): Scope2EnergyType | '' {
  const normalized = value.trim().toLowerCase();
  if (
    normalized === 'electricity' ||
    normalized === 'steam' ||
    normalized === 'heating' ||
    normalized === 'cooling'
  ) {
    return normalized;
  }
  return '';
}

export function formatKgCo2e(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'N/A';
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 6 })} kg CO2e`;
}

export function formatScope2MethodLabel(value: string | null | undefined): string {
  if (value === 'location_based') return 'Location-based';
  if (value === 'market_based') return 'Market-based';
  if (value === 'both') return 'Both';
  return 'N/A';
}

export function buildAssignmentSignature(input: {
  scope: GHGScope;
  activityCategory: string;
  activityMethod: string;
  activityDescription: string;
}): string {
  return [
    String(input.scope),
    input.activityCategory.trim().toLowerCase(),
    input.activityMethod.trim(),
    input.activityDescription.trim().toLowerCase(),
  ].join('|');
}

export function deriveSummaryForMethod(input: {
  activityMethod: string;
  methodValues: Record<string, number>;
  methodUnits: Record<string, string>;
}): { quantity: number; unit: string; display: string } {
  const mapping: Record<string, { valueKey: string; unitKey: string; fixedUnit?: string }> = {
    Money: { valueKey: 'money', unitKey: 'money_unit' },
    Distance: { valueKey: 'distance', unitKey: 'distance_unit' },
    Energy: { valueKey: 'energy', unitKey: 'energy_unit' },
    Volume: { valueKey: 'volume', unitKey: 'volume_unit' },
    Weight: { valueKey: 'weight', unitKey: 'weight_unit' },
    PassengerOverDistance: {
      valueKey: 'passengers',
      unitKey: 'distance_unit',
      fixedUnit: 'passenger',
    },
    WeightOverDistance: { valueKey: 'weight', unitKey: 'weight_unit' },
    ContainerOverDistance: { valueKey: 'containers', unitKey: '', fixedUnit: 'container' },
    AreaOverTime: { valueKey: 'area', unitKey: 'area_unit' },
    DataOverTime: { valueKey: 'data', unitKey: 'data_unit' },
    DistanceOverTime: { valueKey: 'distance', unitKey: 'distance_unit' },
    NumberOverTime: { valueKey: 'number', unitKey: '' },
    WeightOverTime: { valueKey: 'weight', unitKey: 'weight_unit' },
  };

  const preferred = mapping[input.activityMethod];
  let valueKey = preferred?.valueKey ?? '';
  if (!valueKey) {
    valueKey = Object.keys(input.methodValues)[0] ?? '';
  }
  if (!valueKey) {
    return { quantity: 0, unit: '', display: '' };
  }

  const quantity = input.methodValues[valueKey] ?? 0;
  const resolvedUnit =
    preferred?.fixedUnit ??
    (preferred?.unitKey
      ? (input.methodUnits[preferred.unitKey] ?? '')
      : (input.methodUnits[`${valueKey}_unit`] ?? ''));

  return {
    quantity,
    unit: resolvedUnit,
    display: `${String(quantity)} ${resolvedUnit}`.trim(),
  };
}

export function toMethodKeyedStrings(
  values: Record<string, number> | undefined
): Record<string, string> {
  if (!values) return {};
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [key, String(value)])
  );
}

export function toResolvedCandidateFromManualFactor(
  factor:
    | {
        id: string;
        activityId: string;
        name: string;
        source: string;
        region: string;
        regionName: string;
        year: number;
        unit: string;
        unitType: string;
        co2ePerUnit: number;
      }
    | undefined,
  dataVersion: string
): ResolveFactorCandidate | undefined {
  if (!factor) return undefined;
  return {
    ...factor,
    dataVersion,
  };
}
